import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { PageTopBar } from '~components/page-top-bar';
import { TopTabs } from '~components/top-tabs';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { useApplicationSelectionStore, useAuthStore } from '~contexts/mobx';
import { useNavigation } from '~utils/use-navigation';
import { useSearchParams } from '~utils/use-search-params';
import { useTabSearchParam } from '~utils/use-tab-search-param';
import { AskAIButton } from '~analytics-components/ask-ai-button';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { OverviewToolbar } from './overview-toolbar';
import { lastSelectableDay } from '../kpis/periods';
import { usePageFilter } from './use-page-filter';
import type { PageFilterQuery } from './use-page-filter';
import { usePageCoupon } from './use-page-coupon';
import type { PageCouponQuery } from './use-page-coupon';
import {
    ANALYTICS_TABS,
    ANALYTICS_TAB_KEYS,
    DEFAULT_ANALYTICS_TAB,
} from './tabs';
import type { AnalyticsTabKey } from './tabs';
import style from './style.module.scss';

type AnalyticsOverviewQuery = PageFilterQuery &
    PageCouponQuery & { tab?: string };

export const AnalyticsOverviewPage = observer(() => {
    const authStore = useAuthStore();
    const navigation = useNavigation();

    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<AnalyticsOverviewQuery>();
    const {
        filter,
        setPeriod,
        setPreset,
        setRegion,
        setCompare,
    } = usePageFilter({
        search,
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
    });
    const [activeTab, setTab] = useTabSearchParam(
        search,
        ANALYTICS_TAB_KEYS,
        DEFAULT_ANALYTICS_TAB
    );
    // Only that tab shows the selector: elsewhere the list stays unfetched and
    // no default is written to the URL.
    const onAcquisition = activeTab === 'acquisition';
    const listedCoupon = usePageCoupon({ search, enabled: onAcquisition });
    const {
        state: couponListState,
        options: couponOptions,
        setCoupon,
    } = listedCoupon;
    // Gated on the tab too: on Back from Acquisition the list is still `ready`
    // for one render, which would write a default onto the previous entry.
    const defaultCoupon =
        couponOptions.find(
            (option) => option.isPrivate && !option.isUnlimitedRedemption
        ) ??
        couponOptions.find((option) => option.isPrivate) ??
        couponOptions[0];
    const resolvedCouponId =
        onAcquisition && couponListState.status === 'ready'
            ? couponOptions.find(
                  (option) => option.id === listedCoupon.couponId
              )?.id ?? defaultCoupon.id
            : undefined;
    const pageCoupon = { ...listedCoupon, couponId: resolvedCouponId };

    useEffect(() => {
        if (
            resolvedCouponId !== undefined &&
            resolvedCouponId !== listedCoupon.couponId
        ) {
            setCoupon(resolvedCouponId);
        }
    }, [resolvedCouponId, listedCoupon.couponId, setCoupon]);

    const { setParams } = search;
    useEffect(() => {
        if (!onAcquisition && listedCoupon.couponId !== undefined) {
            setParams({ couponId: undefined }, { preserveScroll: true });
        }
    }, [onAcquisition, listedCoupon.couponId, setParams]);

    useEffect(() => {
        if (
            !authStore.hasManagementAccess &&
            !authStore.isCustomerServiceAgent
        ) {
            navigation.goToHome();
        }
    }, [
        authStore.hasManagementAccess,
        authStore.isCustomerServiceAgent,
        navigation,
    ]);

    return (
        <>
            <PageTopBar title="Overview">
                <AskAIButton />
            </PageTopBar>

            <TopTabs
                variant="page-header"
                belowTabBar={
                    <div className={style.filters_block}>
                        <OverviewToolbar
                            filters={
                                <AnalyticsSearchOptions
                                    startDate={filter.range.start}
                                    endDate={filter.range.end}
                                    updateParams={setPeriod}
                                    lastSelectableDay={lastSelectableDay()}
                                    // No `campaign`: the CT/CK Overview reports on the
                                    // apps as a whole, which have no start and no end.
                                    periodPresets={{
                                        active: filter.preset,
                                        onSelect: setPreset,
                                    }}
                                    regionFilter={{
                                        value: filter.provinces,
                                        disabled: filter.regionLocked,
                                        onChange: setRegion,
                                    }}
                                    compareEnabled={filter.compare}
                                    onCompareChange={setCompare}
                                />
                            }
                        />

                        <KpiLoadProgressBar />
                    </div>
                }
                activeKey={activeTab}
                onChange={(key) => setTab(key as AnalyticsTabKey)}
                tabsArray={ANALYTICS_TABS.map(({ key, label, Section }) => ({
                    key,
                    name: label,
                    // antd keeps a leaving pane one more commit: given
                    // children, its cards refetch at the new filter.
                    children:
                        key === activeTab ? (
                            <Section
                                filter={filter}
                                pageCoupon={pageCoupon}
                                onSelectTab={setTab}
                            />
                        ) : null,
                }))}
            />
        </>
    );
});
